import { getHealthOsSurveyForRoute } from "@healthos/schemas";
import type { PrybarSuggestion } from "@prybar/core";

type Screen = Record<string, unknown>;

const hasBrief = (s: Screen) => typeof s.briefHeadline === "string" && s.briefHeadline.length > 0;

// An in-the-moment opener grounded in the time of day. Routes to wellbeing.context.
function timeOpener(hour: number): PrybarSuggestion {
  if (hour < 11)
    return {
      label: "How should I start today?",
      message: "How should I start my day?",
      icon: "sun",
    };
  if (hour >= 20)
    return { label: "Help me wind down", message: "Help me wind down for sleep", icon: "moon" };
  return {
    label: "I feel stressed",
    message: "I feel stressed, what can I do right now?",
    icon: "brain",
  };
}

// Contextual feedback: react to whatever is on this screen. The assistant
// resolves the subject from the [App context] and records a user.feedback
// signal, so it lands somewhere the system reads, not a dead survey.
function feedbackFor(route: string): PrybarSuggestion {
  if (route.startsWith("/snapshot"))
    return {
      label: "This doesn't match how I feel",
      message: "The Snapshot does not match how I actually feel",
      icon: "feedback",
    };

  if (route.startsWith("/connectors"))
    return {
      label: "A source looks wrong",
      message: "One of my connected sources looks wrong",
      icon: "feedback",
    };
  return {
    label: "Give feedback on this",
    message: "I want to give feedback on what I see on this screen",
    icon: "feedback",
  };
}
const HOW_AM_I: PrybarSuggestion = {
  label: "How am I doing?",
  message: "How am I doing today?",
  icon: "heart",
};
const SUMMARISE_BRIEF: PrybarSuggestion = {
  label: "Summarise this week's brief",
  message: "Summarise this week's brief for me",
  icon: "list",
};

function surveySuggestionFor(route: string): PrybarSuggestion {
  const survey = getHealthOsSurveyForRoute(route);
  return {
    label: "Give feedback",
    message: `Open feedback for ${survey.surfaceLabel}.`,
    icon: "survey",
    priority: 20,
    readOnly: true,
    toolCall: {
      name: "survey.prompt",
      args: { route, surveyId: survey.id },
    },
  };
}

// Per-surface suggestions. Each one exercises a real tool: a card-rendering
// read, a natural-language action, or a grounded answer about this screen.
const BY_ROUTE: Record<string, PrybarSuggestion[]> = {
  "/connectors": [
    {
      label: "What's connected?",
      message: "What health data sources are connected?",
      icon: "pulse",
    },
    { label: "Is my data syncing?", message: "Is my connected data syncing?", icon: "chart" },
  ],
  "/explore": [SUMMARISE_BRIEF, HOW_AM_I],
  "/settings": [
    {
      label: "Give feedback on the app",
      message: "I want to give general feedback on the app",
      icon: "feedback",
    },
  ],
};

function routeKeyFor(route: string): string | null {
  if (route === "/" || route.startsWith("/today")) return "/";
  if (route.startsWith("/snapshot")) return "/snapshot";
  return Object.keys(BY_ROUTE).find((r) => route.startsWith(r)) ?? null;
}

// The Snapshot screen leads with the behaviour the user is actually watching
// (published into the screen context by ScorePage), not a hardcoded one.
function snapshotSuggestions(screen: Screen): PrybarSuggestion[] {
  const watching = Array.isArray(screen.watching) ? (screen.watching as string[]) : [];
  const first = watching[0];
  // Lead with the "why" the user is most likely to interrogate: the behaviour
  // actually being watched, falling back to a trend read when nothing is flagged.
  const lead: PrybarSuggestion = first
    ? {
        label: `Why is my ${first.toLowerCase()} watching?`,
        message: `Why is my ${first.toLowerCase()} being watched in my Snapshot?`,
        icon: "bed",
      }
    : {
        label: "How's my sleep trending?",
        message: "How is my sleep trending this week?",
        icon: "pulse",
      };
  return [
    lead,
    {
      label: "What's my Snapshot telling me?",
      message: "What is my Snapshot telling me right now?",
      icon: "chart",
    },
  ];
}

// The Today surface is screen-aware: only offer to read the brief when the
// user actually has one, otherwise fall back to the showcase moves.
function todaySuggestions(route: string, screen: Screen): PrybarSuggestion[] {
  const list: PrybarSuggestion[] = [surveySuggestionFor(route), HOW_AM_I];
  if (hasBrief(screen)) list.push(SUMMARISE_BRIEF);
  return list;
}

export function suggestionsFor(
  route: string,
  screen: Screen = {},
  now: Date = new Date(),
): PrybarSuggestion[] {
  const key = routeKeyFor(route);
  const routed =
    key === "/"
      ? todaySuggestions(route, screen)
      : key === "/snapshot"
        ? [surveySuggestionFor(route), ...snapshotSuggestions(screen)]
        : key
          ? [surveySuggestionFor(route), ...(BY_ROUTE[key] ?? [])]
          : [surveySuggestionFor(route)];
  // Lead with what's useful on this screen plus a grounded opener, then always
  // reserve the final slot for a contextual way to give feedback on what they
  // see, so it is never crowded out by a busy surface.
  const feedback = feedbackFor(route);
  const seen = new Set<string>([feedback.message]);
  const lead: PrybarSuggestion[] = [];
  for (const s of [...routed, timeOpener(now.getHours())]) {
    if (seen.has(s.message)) continue;
    seen.add(s.message);
    lead.push(s);
  }
  return [...lead.slice(0, 4), feedback];
}
